import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileText } from "lucide-react";

export default function TOSAcceptance() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  const loadUser = useCallback(async () => {
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await digitalOcean
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);

    if (profile.has_accepted_tos) {
      navigate("/Discover");
    }
  }, [navigate]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { error } = await digitalOcean
        .from("profiles")
        .update({ has_accepted_tos: true })
        .eq("id", currentUser.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Terms of Service accepted");
      navigate("/Discover");
    },
    onError: () => {
      toast.error("Failed to accept Terms of Service");
    },
  });

  const handleAccept = () => {
    if (!hasAccepted) {
      toast.error("Please check the box to accept the Terms of Service");
      return;
    }
    acceptMutation.mutate();
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 flex items-center justify-center">
      <div className="max-w-4xl w-full bg-card p-8 rounded-lg border border-border">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-card-foreground">Terms of Service</h1>
        </div>

        <div className="bg-muted/50 p-6 rounded max-h-96 overflow-y-auto mb-6">
          <p className="text-sm text-foreground mb-4">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
          
          <h2 className="text-lg font-semibold text-foreground mb-2">1. Agreement to Terms</h2>
          <p className="text-muted-foreground mb-4">
            By using our service, you agree to be bound by these Terms of Service and all applicable laws and regulations.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">2. User Accounts</h2>
          <p className="text-muted-foreground mb-4">
            You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">3. Content Guidelines</h2>
          <p className="text-muted-foreground mb-4">
            Users must not upload content that infringes on intellectual property rights, contains illegal material, or violates community standards.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">4. Service Modifications</h2>
          <p className="text-muted-foreground mb-4">
            We reserve the right to modify or discontinue the service at any time without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">5. Limitation of Liability</h2>
          <p className="text-muted-foreground mb-4">
            We shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.
          </p>
        </div>

        <div className="flex items-center space-x-2 mb-6">
          <Checkbox 
            id="accept" 
            checked={hasAccepted}
            onCheckedChange={setHasAccepted}
          />
          <label
            htmlFor="accept"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I have read and accept the Terms of Service
          </label>
        </div>

        <div className="flex gap-4">
          <Button 
            onClick={handleAccept} 
            className="flex-1"
            disabled={!hasAccepted || acceptMutation.isPending}
            tooltip={!hasAccepted ? "Please check the box to accept" : acceptMutation.isPending ? "Accepting terms..." : "Accept terms and continue"}
          >
            {acceptMutation.isPending ? "Accepting..." : "Accept and Continue"}
          </Button>
          <Button variant="outline" onClick={() => navigate("/TOS")} tooltip="View the full Terms of Service document">
            Read Full Terms
          </Button>
        </div>
      </div>
    </div>
  );
}
